import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";

import type { Database } from "@/integrations/supabase/types";

const InvoerSchema = z.object({
  dienstSlug: z.string().min(1),
  plaatsSlug: z.string().optional(),
});

export type PrijsOptie = {
  label: string;
  eenheid: string;
  laag: number;
  hoog: number;
  materiaalAandeel: number;
  standaardAantal: number;
  maxAantal: number;
};

export type Prijslijst = {
  opties: PrijsOptie[];
  afstandKm: number | null;
};

export const getPrijslijst = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => InvoerSchema.parse(data))
  .handler(async ({ data }): Promise<Prijslijst> => {
    const supabase = createClient<Database>(
      process.env["SUPABASE_URL"]!,
      process.env["SUPABASE_PUBLISHABLE_KEY"] ?? process.env["SUPABASE_ANON_KEY"]!,
      { auth: { persistSession: false, autoRefreshToken: false } },
    );

    const { data: rijen } = await supabase
      .from("prijsopties")
      .select("label, eenheid, prijs_laag, prijs_hoog, materiaal_aandeel, standaard_aantal, max_aantal")
      .eq("dienst_slug", data.dienstSlug)
      .eq("actief", true)
      .order("sortering", { ascending: true });

    let afstandKm: number | null = null;
    if (data.plaatsSlug) {
      // Plaatsafstanden zijn niet publiek leesbaar; alleen serverzijde opvragen.
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: plaats } = await supabaseAdmin
        .from("plaats_afstanden")
        .select("afstand_km")
        .eq("plaats_slug", data.plaatsSlug)
        .maybeSingle();
      afstandKm = plaats?.afstand_km ?? null;
    }

    return {
      opties: (rijen ?? []).map((r) => ({
        label: r.label,
        eenheid: r.eenheid,
        laag: Number(r.prijs_laag),
        hoog: Number(r.prijs_hoog),
        materiaalAandeel: Number(r.materiaal_aandeel),
        standaardAantal: r.standaard_aantal,
        maxAantal: r.max_aantal,
      })),
      afstandKm,
    };
  });
